import type { AimSettings, Leaderboard, LeetifyBoard, Scope } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.ts";

const key = (scope: Scope) => ["leaderboard", scope] as const;

/** The team's numbers; while the server fetches new ones, asks again every few seconds. */
export const useLeaderboard = (scope: Scope) =>
  useQuery({
    queryKey: key(scope),
    queryFn: () => api<Leaderboard>("GET", `/leaderboard?scope=${encodeURIComponent(scope)}`),
    refetchInterval: (q) => (q.state.data?.refreshing ? 3000 : false),
  });

/**
 * Leetify, asked live each time the tab opens (or Refresh is pressed): the
 * server keeps none of it, and neither does this page once the tab closes.
 */
export const useLeetify = (scope: Scope, enabled: boolean) =>
  useQuery({
    queryKey: ["leaderboard-leetify", scope],
    queryFn: () => api<LeetifyBoard>("GET", `/leaderboard/leetify?scope=${encodeURIComponent(scope)}`),
    enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

export function useRefreshStats(scope: Scope) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<Leaderboard>("POST", `/leaderboard/refresh?scope=${encodeURIComponent(scope)}`),
    onSuccess: (data) => qc.setQueryData(key(scope), data),
  });
}

export function useHideMe(scope: Scope) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hidden: boolean) => api<{ hidden: boolean }>("PUT", "/leaderboard/hidden", { scope, hidden }),
    onSettled: () => qc.invalidateQueries({ queryKey: key(scope) }),
  });
}

/** My DPI and sensitivity: every library's board shows them. */
export function useSaveAim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aim: AimSettings) => api<AimSettings>("PUT", "/me/aim", aim),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leaderboard"] }),
  });
}
